import { buildUrl, request } from './client';
import type {
  ActivityPage,
  AddressCheck,
  AddressConfirmation,
  AssistantAnswer,
  AssistantVoiceSession,
  AssistantVoiceStart,
  AssistantVoiceStatus,
  MapAreaShapes,
  SameNumberLead,
  Owner,
  Household,
  LeadObject,
  LeadObjectInput,
  OwnerResidence,
  OwnerResidenceInput,
  AssistantQuestion,
  AddressCheckAcceptResult,
  AddressCheckSummary,
  CallQualityReview,
  ConversationInsights,
  ConversationReport,
  LiveAssistStart,
  QualitySummary,
  Booking,
  BookingsStatus,
  CalendarConnection,
  CommissionList,
  Deal,
  DealCreate,
  DealUpdate,
  Inquiry,
  InquiryObject,
  MailboxConnection,
  Project,
  ProjectCreate,
  SalesNode,
  SalesOverview,
  SalesPerson,
  SalesPlace,
  DocumentSignature,
  PublicSigning,
  ContractCockpit,
  CreateContractVariantsInput,
  CreateContractVariantsResult,
  ExposeTextSuggestion,
  AddressValidation,
  PlaceAddress,
  PlacePrediction,
  PlaceResult,
  BrokerCard,
  DocumentRequest,
  DocumentRequestInput,
  DocumentRequestLetters,
  DocumentRequestSendResult,
  PropertyManager,
  BrokerProfile,
  Expose,
  ExposeDetail,
  ExposeImage,
  ExposeImageInput,
  ExposeInput,
  DocumentTemplate,
  DocumentTemplateInput,
  LeadDocument,
  LeadDocumentDetail,
  LeadDocumentInput,
  SystemNotice,
  SystemNoticeInput,
  AdminJob,
  DayRoute,
  EmailBounce,
  EmailMessage,
  EmailPage,
  EmailTemplate,
  FeatureFlag,
  IntegrationStatus,
  MapAppointment,
  MapArea,
  MapLeadClusters,
  NotificationSummary,
  PrivacyImpact,
  PrivacyRequest,
  PrivacyRequestPage,
  Role,
  RouteSettings,
  SlotRecommendation,
  SlotRequest,
  SystemStatus,
  Ticket,
  TicketCategory,
  TicketCategoryInput,
  TicketComment,
  TicketCreate,
  TicketDetail,
  TicketPage,
  TicketUpdate,
  UserNotificationSettings,
  UserNotificationSettingsInput,
  UserSummary,
  AIAction,
  AIProposal,
  AIReviews,
  Appointment,
  AppointmentCreate,
  AppointmentDetail,
  AppointmentHandback,
  AppointmentHandbackRequest,
  AppointmentHandbackResolve,
  AppointmentHistory,
  AppointmentSuggestion,
  AppointmentSuggestRequest,
  AppointmentType,
  AppointmentTypeInput,
  Area,
  AreaElementType,
  AreaUserAssignment,
  CalendarRange,
  CalendarSlot,
  Call,
  CallAnalysis,
  CallDetail,
  CallOutcome,
  County,
  Dashboard,
  TeamOverview,
  TelesalesTargets,
  TelesalesScope,
  AppointmentConfirmation,
  DayView,
  BrokerLeadsView,
  LeadUpdate,
  PhoneConfirmationInput,
  PhoneConfirmationItem,
  AppointmentReport,
  AppointmentReportAnswers,
  AppointmentReportDue,
  BuyerMatchList,
  LeadBuyerMatch,
  LeadAttachment,
  BuyerRequest,
  BuyerRequestUpdate,
  PartnerProspect,
  PartnerProspectInput,
  PartnerProspectEvent,
  PartnerProspectEventInput,
  PartnerMailTemplate,
  PartnerMailSender,
  PartnerMailDraft,
  PartnerMailSend,
  OwnPublicBookingPage,
  OwnBookInput,
  OwnBooking,
  OwnBookingPage,
  OwnBookingPageInput,
  CallLeadSuggestion,
  LeadCreateInput,
  FederalState,
  FollowUp,
  FollowUpRequest,
  FollowUpCreate,
  GeoCorrection,
  GeoReviewPage,
  GeoStatus,
  ItemList,
  Lead,
  LeadFocus,
  LeadPage,
  LeadStatus,
  LockInfo,
  LoginResponse,
  Member,
  Municipality,
  NotificationPage,
  OutlookStatus,
  ParseResult,
  PostalCode,
  PostalCodeStat,
  ProjectSettings,
  QueueNext,
  QueuePeek,
  QueueStats,
  RegionStat,
  Responsibility,
  ResponsibilityView,
  StoredBriefResponse,
  DossierQueued,
  SyncItem,
  TelephonyAgent,
  TelephonyAgentResponse,
  BrowserToken,
  UserContext,
  PriceList,
  BrokerContractView,
  BrokerContractInput,
  InvitationInfo,
  UserInvitation,
  Mandate,
  MandateInput,
  MandateView,
  Invoice,
  InvoiceDraftInput,
  InvoiceIssueResult,
} from './types';

// Filter der Leadliste; Namen entsprechen den Query-Parametern der API.
export interface LeadFilters {
  q?: string;
  status?: string[];
  geo_status?: string[];
  data_quality?: string[];
  postal_code?: string;
  federal_state_id?: string;
  county_id?: string;
  area_id?: string;
  assigned_user_id?: string;
  assigned_field_sales_user_id?: string;
  // Makler-Sicht „Meine Leads“: Leads mit Terminen dieses Maklers (nicht in der URL, fest gesetzt).
  appointment_user_id?: string;
  has_appointment?: string;
  has_follow_up?: string;
  next_contact_from?: string;
  next_contact_to?: string;
  last_contact_from?: string;
  last_contact_to?: string;
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: string;
}

export interface LeadListParams extends LeadFilters {
  cursor?: string;
  limit?: number;
}

export type RegionLevel = 'federal-states' | 'counties' | 'areas';

export interface AreaInput {
  name?: string;
  description?: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

export interface AreaUserInput {
  user_id: string;
  project_id?: string;
  responsibility?: Responsibility;
  valid_from?: string;
  valid_until?: string;
  is_primary?: boolean;
}

export type FollowUpDue = 'all' | 'overdue' | 'today' | 'upcoming';

export interface FollowUpQuery {
  assigned?: string;
  status?: 'open' | 'done' | 'all';
  due?: FollowUpDue;
  lead_id?: string;
  area_id?: string;
  field_sales_user_id?: string;
}

export interface CalendarQuery {
  from: string;
  to: string;
  user_id?: string;
  area_id?: string;
  county_id?: string;
  status?: string[];
  // Nur Termine ohne Makler.
  unassigned?: 'true';
}

export interface RescheduleInput {
  start_at: string;
  duration_minutes?: number;
  reason?: string;
  allow_conflicts?: boolean;
}

const project = (projectId: string) => `/projects/${encodeURIComponent(projectId)}`;
const area = (areaId: string) => `/areas/${encodeURIComponent(areaId)}`;
const id = (v: string) => encodeURIComponent(v);

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: (signal?: AbortSignal) => request<UserContext>('/me', { signal }),
  setActiveProject: (projectId: string) =>
    request<void>('/me/active-project', { method: 'PUT', body: { project_id: projectId } }),
  leadStatuses: (signal?: AbortSignal) => request<ItemList<LeadStatus>>('/lead-statuses', { signal }),

  // Projektbezogene Endpunkte (Projekt-ID aus dem aktiven Projektkontext).
  leads: (projectId: string, params: LeadListParams = {}, signal?: AbortSignal) =>
    request<LeadPage>(`${project(projectId)}/leads`, { query: { ...params }, signal }),
  lead: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<Lead>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}`, { signal }),
  // Lead anlegen (Terminierer, Support, Leitung – z. B. neuer Eigentümer am Telefon).
  createLead: (projectId: string, body: LeadCreateInput) => request<Lead>(`${project(projectId)}/leads`, { method: 'POST', body }),
  // Verpasster Anruf unbekannter Nummer: Vorschläge und „Diesem Objekt zuordnen“.
  callLeadSuggestions: (projectId: string, callId: string, signal?: AbortSignal) =>
    request<ItemList<CallLeadSuggestion>>(`${project(projectId)}/calls/${encodeURIComponent(callId)}/lead-suggestions`, { signal }),
  assignCallToLead: (projectId: string, callId: string, body: { lead_id: string; save_number: boolean }) =>
    request<Call>(`${project(projectId)}/calls/${encodeURIComponent(callId)}/assign-lead`, { method: 'POST', body }),
  // Stammdaten ändern (lead.edit): nur geänderte Felder, null löscht (Thomas 12.10.2026: E-Mail und Co. in der Akquise).
  updateLead: (projectId: string, leadId: string, body: LeadUpdate) =>
    request<Lead>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}`, { method: 'PATCH', body }),
  // Notiz an den Lead hängen (mit Datum und Verfasser, auch ohne Anruf).
  appendLeadNote: (projectId: string, leadId: string, text: string, appointmentId?: string) =>
    request<Lead>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}/notes`, {
      method: 'POST',
      body: { text, ...(appointmentId ? { appointment_id: appointmentId } : {}) },
    }),
  members: (projectId: string, signal?: AbortSignal) => request<ItemList<Member>>(`${project(projectId)}/members`, { signal }),
  regionStats: (projectId: string, level: RegionLevel, federalStateId?: string, signal?: AbortSignal) =>
    request<ItemList<RegionStat>>(`${project(projectId)}/geo/stats/${level}`, {
      query: { federal_state_id: federalStateId },
      signal,
    }),
  postalCodeStats: (projectId: string, countyId: string, signal?: AbortSignal) =>
    request<ItemList<PostalCodeStat>>(`${project(projectId)}/geo/stats/postal-codes`, { query: { county_id: countyId }, signal }),
  geoReview: (projectId: string, params: { cursor?: string; limit?: number; geoStatus?: (GeoStatus | 'ASSUMED')[] }, signal?: AbortSignal) =>
    request<GeoReviewPage>(`${project(projectId)}/geo/review`, {
      query: { cursor: params.cursor, limit: params.limit, geo_status: params.geoStatus },
      signal,
    }),
  geoReviewSummary: (projectId: string, signal?: AbortSignal) =>
    request<{ counts: Record<string, number>; address_checks?: AddressCheckSummary }>(`${project(projectId)}/geo/review/summary`, { signal }),
  startAddressChecks: (projectId: string) =>
    request<AddressCheckSummary>(`${project(projectId)}/geo/address-checks`, { method: 'POST' }),
  addressChecks: (projectId: string, status?: AddressCheck['status'], signal?: AbortSignal) =>
    request<ItemList<AddressCheck>>(`${project(projectId)}/geo/address-checks`, { query: { status }, signal }),
  acceptAddressChecks: (projectId: string, leadIds: string[]) =>
    request<AddressCheckAcceptResult>(`${project(projectId)}/geo/address-checks/accept`, { method: 'POST', body: { lead_ids: leadIds } }),
  correctGeo: (projectId: string, leadId: string, body: GeoCorrection) =>
    request<Lead>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}/geo`, { method: 'POST', body }),
  confirmAddress: (projectId: string, leadId: string, body: AddressConfirmation) =>
    request<Lead>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}/address-confirmation`, { method: 'POST', body }),
  askAssistant: (projectId: string, body: AssistantQuestion) =>
    request<AssistantAnswer>(`${project(projectId)}/assistant/messages`, { method: 'POST', body }),
  assistantVoiceStatus: (projectId: string, signal?: AbortSignal) => request<AssistantVoiceStatus>(`${project(projectId)}/assistant/voice`, { signal }),
  startAssistantVoice: (projectId: string, body: AssistantVoiceStart) =>
    request<AssistantVoiceSession>(`${project(projectId)}/assistant/voice`, { method: 'POST', body }),
  assistantVoiceTalked: (projectId: string, sessionId: string) =>
    request<AssistantVoiceStatus>(`${project(projectId)}/assistant/voice/${encodeURIComponent(sessionId)}/talked`, { method: 'POST' }),
  responsibilities: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ResponsibilityView>(`${project(projectId)}/leads/${encodeURIComponent(leadId)}/responsibilities`, { signal }),
  resolveGeo: (projectId: string, mode: 'UNPROCESSED' | 'PROBLEMS' | 'ALL', assignFieldSales = false) =>
    request<{ job_id: string; mode: string }>(`${project(projectId)}/geo/resolve`, {
      method: 'POST',
      body: { mode, assign_field_sales: assignFieldSales },
    }),
  projectAreas: (projectId: string, includeInactive = false, signal?: AbortSignal) =>
    request<ItemList<Area>>(`${project(projectId)}/areas`, { query: { include_inactive: includeInactive || undefined }, signal }),

  // Gebiete (Berechtigung wird serverseitig je Gebiet geprüft).
  area: (areaId: string, projectId?: string, signal?: AbortSignal) =>
    request<Area>(area(areaId), { query: { project_id: projectId }, signal }),
  createArea: (body: AreaInput & { name: string; project_id: string }) =>
    request<Area>('/areas', { method: 'POST', body }),
  updateArea: (areaId: string, body: AreaInput) => request<Area>(area(areaId), { method: 'PATCH', body }),
  archiveArea: (areaId: string) => request<void>(area(areaId), { method: 'DELETE' }),
  addAreaElement: (areaId: string, element: { type: AreaElementType; reference_id?: string; code?: string }) =>
    request<Area>(`${area(areaId)}/assignments`, { method: 'POST', body: element }),
  removeAreaElement: (areaId: string, elementId: string) =>
    request<Area>(`${area(areaId)}/assignments/${encodeURIComponent(elementId)}`, { method: 'DELETE' }),
  addAreaUser: (areaId: string, body: AreaUserInput) =>
    request<AreaUserAssignment>(`${area(areaId)}/users`, { method: 'POST', body }),
  endAreaUser: (areaId: string, assignmentId: string) =>
    request<void>(`${area(areaId)}/users/${encodeURIComponent(assignmentId)}`, { method: 'DELETE' }),

  // Geo-Referenzdaten (geo.read).
  federalStates: (signal?: AbortSignal) => request<ItemList<FederalState>>('/geo/federal-states', { signal }),
  counties: (params: { federalStateId?: string; q?: string }, signal?: AbortSignal) =>
    request<ItemList<County>>('/geo/counties', { query: { federal_state_id: params.federalStateId, q: params.q }, signal }),
  municipalities: (params: { q?: string; countyId?: string; limit?: number }, signal?: AbortSignal) =>
    request<ItemList<Municipality>>('/geo/municipalities', {
      query: { q: params.q, county_id: params.countyId, limit: params.limit },
      signal,
    }),
  postalCodes: (prefix: string, signal?: AbortSignal) =>
    request<ItemList<PostalCode>>('/geo/postal-codes', { query: { postal_code: prefix, limit: 20 }, signal }),

  // Benutzerbezogen: Benachrichtigungen, Telefonie-Endpunkt, Outlook.
  notifications: (params: { unread?: boolean; cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<NotificationPage>('/me/notifications', { query: { unread: params.unread || undefined, cursor: params.cursor, limit: params.limit }, signal }),
  unreadCount: (signal?: AbortSignal) => request<{ count: number }>('/me/notifications/unread-count', { signal }),
  readNotification: (notificationId: string) => request<void>(`/me/notifications/${id(notificationId)}/read`, { method: 'POST' }),
  readAllNotifications: () => request<{ updated: number }>('/me/notifications/read-all', { method: 'POST' }),
  telephonyAgent: (signal?: AbortSignal) => request<TelephonyAgentResponse>('/me/telephony', { signal }),
  // Hinweise (erster Login) und Maklerdaten.
  acknowledgeNotice: (key: string, version: number) =>
    request<void>(`/me/notices/${encodeURIComponent(key)}/acknowledge`, { method: 'POST', body: { version } }),
  notice: (key: string, signal?: AbortSignal) => request<SystemNotice>(`/notices/${encodeURIComponent(key)}`, { signal }),
  saveNotice: (key: string, body: SystemNoticeInput) => request<SystemNotice>(`/notices/${encodeURIComponent(key)}`, { method: 'PUT', body }),
  brokerProfile: (signal?: AbortSignal) => request<BrokerProfile>('/me/broker-profile', { signal }),
  saveBrokerProfile: (body: BrokerProfile) => request<BrokerProfile>('/me/broker-profile', { method: 'PUT', body }),
  uploadBrokerPhoto: (file: Blob) => request<BrokerProfile>('/me/broker-profile/photo', { method: 'PUT', body: file }),
  deleteBrokerPhoto: () => request<void>('/me/broker-profile/photo', { method: 'DELETE' }),
  // Partnerprofile durch die Benutzerverwaltung.
  userBrokerProfile: (userId: string, signal?: AbortSignal) => request<BrokerProfile>(`/users/${id(userId)}/broker-profile`, { signal }),
  saveUserBrokerProfile: (userId: string, body: BrokerProfile) => request<BrokerProfile>(`/users/${id(userId)}/broker-profile`, { method: 'PUT', body }),
  uploadUserBrokerPhoto: (userId: string, file: Blob) => request<BrokerProfile>(`/users/${id(userId)}/broker-profile/photo`, { method: 'PUT', body: file }),
  deleteUserBrokerPhoto: (userId: string) => request<void>(`/users/${id(userId)}/broker-profile/photo`, { method: 'DELETE' }),
  saveTelephonyAgent: (body: TelephonyAgent) => request<TelephonyAgentResponse>('/me/telephony', { method: 'PUT', body }),
  browserToken: (projectId: string) => request<BrowserToken>(`${project(projectId)}/telephony/browser-token`, { method: 'POST' }),
  outlookStatus: (signal?: AbortSignal) => request<OutlookStatus>('/integrations/outlook', { signal }),
  connectOutlook: (returnTo: string) =>
    request<{ authorization_url: string }>('/integrations/outlook/connect', { method: 'POST', body: { return_to: returnTo } }),
  disconnectOutlook: () => request<void>('/integrations/outlook', { method: 'DELETE' }),
  adminJobs: (status = 'FAILED', signal?: AbortSignal) => request<ItemList<AdminJob>>('/admin/jobs', { query: { status }, signal }),
  retryJob: (jobId: string) => request<void>(`/admin/jobs/${id(jobId)}/retry`, { method: 'POST' }),
  cancelJob: (jobId: string) => request<void>(`/admin/jobs/${id(jobId)}/cancel`, { method: 'POST' }),

  // Akquise und Telefonie (projektbezogen).
  dashboard: (projectId: string, signal?: AbortSignal) => request<Dashboard>(`${project(projectId)}/dashboard`, { signal }),
  teamOverview: (projectId: string, signal?: AbortSignal) => request<TeamOverview>(`${project(projectId)}/team-overview`, { signal }),
  appointmentConfirmation: (projectId: string, appointmentId: string, signal?: AbortSignal) =>
    request<AppointmentConfirmation>(`${project(projectId)}/appointments/${id(appointmentId)}/confirmation`, { signal }),
  // copyToBroker: beim Senden Kopie an den Makler (Dominik 13.10.2026).
  updateAppointmentConfirmation: (projectId: string, appointmentId: string, action: 'send' | 'off' | 'auto', copyToBroker = false) =>
    request<AppointmentConfirmation>(`${project(projectId)}/appointments/${id(appointmentId)}/confirmation`, {
      method: 'POST',
      body: { action, ...(copyToBroker ? { copy_to_broker: true } : {}) },
    }),
  setTelesalesScope: (projectId: string, userId: string, body: { field_sales_user_ids: string[]; area_ids: string[] }) =>
    request<TelesalesScope>(`${project(projectId)}/telesales-scopes/${id(userId)}`, { method: 'PUT', body }),
  setAppointmentGoal: (projectId: string, userId: string, goal: '' | 'ON_SITE' | 'REMOTE') =>
    request<void>(`${project(projectId)}/field-sales/${id(userId)}/appointment-goal`, { method: 'PUT', body: { goal } }),
  setTelesalesTargets: (projectId: string, userId: string, body: TelesalesTargets) =>
    request<{ targets: TelesalesTargets | null }>(`${project(projectId)}/telesales-targets/${id(userId)}`, { method: 'PUT', body }),
  settings: (projectId: string, signal?: AbortSignal) => request<ProjectSettings>(`${project(projectId)}/settings`, { signal }),
  saveSettings: (projectId: string, body: Partial<ProjectSettings>) =>
    request<ProjectSettings>(`${project(projectId)}/settings`, { method: 'PUT', body }),
  queueNext: (projectId: string, body: { skip_lead_id?: string; release_current?: boolean; field_sales_user_id?: string | null } = {}) =>
    request<QueueNext>(`${project(projectId)}/queue/next`, { method: 'POST', body }),
  queuePeek: (projectId: string, exclude?: string, signal?: AbortSignal) =>
    request<QueuePeek>(`${project(projectId)}/queue/peek`, { query: { limit: 3, exclude }, signal }),
  queueStats: (projectId: string, signal?: AbortSignal) => request<QueueStats>(`${project(projectId)}/queue/stats`, { signal }),
  focus: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<LeadFocus>(`${project(projectId)}/leads/${id(leadId)}/focus`, { signal }),
  lockLead: (projectId: string, leadId: string) => request<LockInfo>(`${project(projectId)}/leads/${id(leadId)}/lock`, { method: 'POST' }),
  unlockLead: (projectId: string, leadId: string) => request<void>(`${project(projectId)}/leads/${id(leadId)}/lock`, { method: 'DELETE' }),
  // Eigentümer des Objekts (B-17) und ihr Wohnort.
  leadOwners: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<Owner> & { household?: Household }>(`${project(projectId)}/leads/${id(leadId)}/owners`, { signal }),
  getOwner: (projectId: string, ownerId: string, signal?: AbortSignal) => request<Owner>(`${project(projectId)}/owners/${id(ownerId)}`, { signal }),
  // Objekte des Leads (B-20): Hauptobjekt wie am Lead, Einheit und Grundbuch am Objekt.
  leadObjects: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<LeadObject>>(`${project(projectId)}/leads/${id(leadId)}/objects`, { signal }),
  updateLeadObject: (projectId: string, leadId: string, objectId: string, body: LeadObjectInput) =>
    request<LeadObject>(`${project(projectId)}/leads/${id(leadId)}/objects/${id(objectId)}`, { method: 'PATCH', body }),
  splitLeadObject: (projectId: string, leadId: string, objectId: string) =>
    request<{ lead_id: string }>(`${project(projectId)}/leads/${id(leadId)}/objects/${id(objectId)}/split`, { method: 'POST' }),
  setOwnerResidence: (projectId: string, ownerId: string, body: OwnerResidenceInput) =>
    request<OwnerResidence>(`${project(projectId)}/owners/${id(ownerId)}/residence`, { method: 'PUT', body }),
  sameNumberLeads: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<SameNumberLead>>(`${project(projectId)}/leads/${id(leadId)}/same-number`, { signal }),
  activities: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ActivityPage>(`${project(projectId)}/leads/${id(leadId)}/activities`, { query: { limit: 30 }, signal }),
  leadCalls: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<Call>>(`${project(projectId)}/leads/${id(leadId)}/calls`, { query: { limit: 20 }, signal }),
  startCall: (projectId: string, body: { lead_id: string; phone?: string; override_policy?: boolean; override_reason?: string; appointment_id?: string }) =>
    request<Call>(`${project(projectId)}/calls`, { method: 'POST', body }),
  activeCall: (projectId: string, signal?: AbortSignal) => request<{ call: Call | null }>(`${project(projectId)}/calls/active`, { signal }),
  call: (projectId: string, callId: string, signal?: AbortSignal) =>
    request<CallDetail>(`${project(projectId)}/calls/${id(callId)}`, { signal }),
  endCall: (projectId: string, callId: string) => request<Call>(`${project(projectId)}/calls/${id(callId)}/end`, { method: 'POST' }),
  // Makler-Dokumente.
  documentTemplates: (projectId: string, all = false, signal?: AbortSignal) =>
    request<{ items: DocumentTemplate[] }>(`${project(projectId)}/document-templates${all ? '?all=true' : ''}`, { signal }),
  createDocumentTemplate: (projectId: string, body: DocumentTemplateInput) =>
    request<DocumentTemplate>(`${project(projectId)}/document-templates`, { method: 'POST', body }),
  updateDocumentTemplate: (projectId: string, templateId: string, body: DocumentTemplateInput) =>
    request<DocumentTemplate>(`${project(projectId)}/document-templates/${id(templateId)}`, { method: 'PUT', body }),
  leadDocuments: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<{ items: LeadDocument[] }>(`${project(projectId)}/leads/${id(leadId)}/documents`, { signal }),
  createLeadDocument: (projectId: string, leadId: string, body: { template_id: string; appointment_id?: string | null }) =>
    request<LeadDocumentDetail>(`${project(projectId)}/leads/${id(leadId)}/documents`, { method: 'POST', body }),
  createContractVariants: (projectId: string, leadId: string, body: CreateContractVariantsInput) =>
    request<CreateContractVariantsResult>(`${project(projectId)}/leads/${id(leadId)}/contract-variants`, { method: 'POST', body }),
  updateDocumentApproval: (projectId: string, documentId: string, body: { action: 'SUBMIT' | 'APPROVE' | 'REJECT'; note?: string }) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}/approval`, { method: 'POST', body }),
  markDocumentSigned: (projectId: string, documentId: string, body: { signer_name?: string; note?: string }) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}/mark-signed`, { method: 'POST', body }),
  contractCockpit: (projectId: string, signal?: AbortSignal) =>
    request<ContractCockpit>(`${project(projectId)}/contract-cockpit`, { signal }),
  setContractApprovalSetting: (projectId: string, requireContractApproval: boolean) =>
    request<ContractCockpit>(`${project(projectId)}/contract-approval-setting`, {
      method: 'PUT',
      body: { require_contract_approval: requireContractApproval },
    }),
  leadDocument: (projectId: string, documentId: string, signal?: AbortSignal) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}`, { signal }),
  updateLeadDocument: (projectId: string, documentId: string, body: LeadDocumentInput) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}`, { method: 'PUT', body }),
  deleteLeadDocument: (projectId: string, documentId: string) => request<void>(`${project(projectId)}/documents/${id(documentId)}`, { method: 'DELETE' }),
  // Digitale Unterschrift: Anfrage (E-Mail oder Gerät), öffentliche Seite ohne Anmeldung.
  requestSignature: (projectId: string, documentId: string, body: { signer_name: string; signer_email?: string; channel: 'EMAIL' | 'DEVICE' }) =>
    request<{ signature: DocumentSignature; sign_url: string | null }>(`${project(projectId)}/documents/${id(documentId)}/signature-requests`, {
      method: 'POST',
      body,
    }),
  cancelSignature: (projectId: string, documentId: string) =>
    request<void>(`${project(projectId)}/documents/${id(documentId)}/signature-requests`, { method: 'DELETE' }),
  // Eigenes Termintool (Dominik 24.10.2026): öffentliche Buchungsseite, Buchen, Verwalten.
  publicBookingPage: (slug: string, query: { from?: string; to?: string; host?: string }, signal?: AbortSignal) =>
    request<OwnPublicBookingPage>(`/public/booking/${encodeURIComponent(slug)}`, { query, signal }),
  publicBook: (slug: string, body: OwnBookInput) => request<OwnBooking>(`/public/booking/${encodeURIComponent(slug)}`, { method: 'POST', body }),
  publicBookingManage: (token: string, signal?: AbortSignal) => request<OwnBooking>(`/public/booking/manage/${encodeURIComponent(token)}`, { signal }),
  publicBookingCancel: (token: string, reason: string) => request<OwnBooking>(`/public/booking/manage/${encodeURIComponent(token)}/cancel`, { method: 'POST', body: { reason } }),
  bookingPages: (projectId: string, signal?: AbortSignal) => request<ItemList<OwnBookingPage>>(`${project(projectId)}/booking-pages`, { signal }),
  upsertBookingPage: (projectId: string, body: OwnBookingPageInput) => request<OwnBookingPage>(`${project(projectId)}/booking-pages`, { method: 'PUT', body }),
  bookings: (projectId: string, signal?: AbortSignal) => request<ItemList<OwnBooking>>(`${project(projectId)}/booking-pages/bookings`, { signal }),
  publicSignature: (token: string, signal?: AbortSignal) => request<PublicSigning>(`/public/signatures/${encodeURIComponent(token)}`, { signal }),
  signPublic: (token: string, body: { name: string; signature: string; agree: boolean; early_start: boolean }) =>
    request<PublicSigning>(`/public/signatures/${encodeURIComponent(token)}/sign`, { method: 'POST', body }),
  finalizeLeadDocument: (projectId: string, documentId: string) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}/finalize`, { method: 'POST' }),
  duplicateLeadDocument: (projectId: string, documentId: string) =>
    request<LeadDocumentDetail>(`${project(projectId)}/documents/${id(documentId)}/duplicate`, { method: 'POST' }),
  // Ortssuche (Google Places): Adressvorschläge, gewählte Adresse, Firmensuche.
  placesAutocomplete: (projectId: string, input: string, session: string, country = 'DE', signal?: AbortSignal) =>
    request<{ items: PlacePrediction[] }>(`${project(projectId)}/places/autocomplete`, { signal, query: { input, session, country } }),
  placesAddress: (projectId: string, placeId: string, session: string) =>
    request<PlaceAddress>(`${project(projectId)}/places/address`, { query: { place_id: placeId, session } }),
  placesValidate: (projectId: string, body: { street: string; house_number: string; postal_code: string; city: string; country_code?: string }) =>
    request<AddressValidation>(`${project(projectId)}/places/validate`, { method: 'POST', body }),
  placesSearch: (projectId: string, q: string, signal?: AbortSignal) =>
    request<{ items: PlaceResult[] }>(`${project(projectId)}/places/search`, { signal, query: { q } }),
  // Unterlagen-Konfigurator und Hausverwaltungen.
  documentRequest: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<DocumentRequest>(`${project(projectId)}/leads/${id(leadId)}/document-request`, { signal }),
  saveDocumentRequest: (projectId: string, leadId: string, body: DocumentRequestInput) =>
    request<DocumentRequest>(`${project(projectId)}/leads/${id(leadId)}/document-request`, { method: 'PUT', body }),
  uploadPowerOfAttorney: (projectId: string, leadId: string, file: Blob, name: string) =>
    request<DocumentRequest>(`${project(projectId)}/leads/${id(leadId)}/document-request/power-of-attorney`, { method: 'PUT', body: file, query: { name } }),
  sendDocumentRequest: (projectId: string, leadId: string) =>
    request<DocumentRequestSendResult>(`${project(projectId)}/leads/${id(leadId)}/document-request/send`, { method: 'POST' }),
  uploadDocumentRequestFile: (projectId: string, leadId: string, code: string, file: Blob, name: string) =>
    request<DocumentRequest>(`${project(projectId)}/leads/${id(leadId)}/document-request/items/${encodeURIComponent(code)}/file`, {
      method: 'PUT',
      body: file,
      query: { name },
    }),
  documentRequestLetters: (projectId: string, leadId: string) =>
    request<DocumentRequestLetters>(`${project(projectId)}/leads/${id(leadId)}/document-request/letters`),
  createOwnerSheet: (projectId: string, leadId: string) =>
    request<LeadDocumentDetail>(`${project(projectId)}/leads/${id(leadId)}/document-request/owner-sheet`, { method: 'POST' }),
  updateDocumentRequestItem: (projectId: string, leadId: string, code: string, status: 'OPEN' | 'REQUESTED' | 'RECEIVED') =>
    request<DocumentRequest>(`${project(projectId)}/leads/${id(leadId)}/document-request/items/${encodeURIComponent(code)}`, { method: 'PUT', body: { status } }),
  propertyManagers: (projectId: string, q: string, signal?: AbortSignal) =>
    request<{ items: PropertyManager[] }>(`${project(projectId)}/property-managers`, { signal, query: { q } }),
  createPropertyManager: (projectId: string, body: PropertyManager) =>
    request<PropertyManager>(`${project(projectId)}/property-managers`, { method: 'POST', body }),
  updatePropertyManager: (projectId: string, managerId: string, body: PropertyManager) =>
    request<PropertyManager>(`${project(projectId)}/property-managers/${id(managerId)}`, { method: 'PUT', body }),
  // Exposés.
  brokers: (projectId: string, signal?: AbortSignal) => request<{ items: BrokerCard[] }>(`${project(projectId)}/brokers`, { signal }),
  deals: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<Deal>>(`${project(projectId)}/deals`, { query: { lead_id: leadId }, signal }),
  createDeal: (projectId: string, body: DealCreate) => request<Deal>(`${project(projectId)}/deals`, { method: 'POST', body }),
  updateDeal: (projectId: string, dealId: string, body: DealUpdate) =>
    request<Deal>(`${project(projectId)}/deals/${id(dealId)}`, { method: 'PATCH', body }),
  closeDeal: (projectId: string, dealId: string) => request<Deal>(`${project(projectId)}/deals/${id(dealId)}/close`, { method: 'POST' }),
  cancelDeal: (projectId: string, dealId: string, reason: string) =>
    request<Deal>(`${project(projectId)}/deals/${id(dealId)}/cancel`, { method: 'POST', body: { reason } }),
  // Microsoft Bookings (Klärung) und Immobilienanfragen.
  ticketBookings: (projectId: string, ticketId: string, signal?: AbortSignal) =>
    request<ItemList<Booking>>(`${project(projectId)}/bookings`, { query: { ticket_id: ticketId }, signal }),
  assignBooking: (projectId: string, bookingId: string, body: { lead_id?: string; create_lead?: boolean }) =>
    request<Booking>(`${project(projectId)}/bookings/${id(bookingId)}/assign`, { method: 'POST', body }),
  inquiries: (projectId: string, q: { lead_id?: string; ticket_id?: string }, signal?: AbortSignal) =>
    request<ItemList<Inquiry>>(`${project(projectId)}/inquiries`, { query: q, signal }),
  inquiryObjects: (projectId: string, q: string, signal?: AbortSignal) =>
    request<ItemList<InquiryObject>>(`${project(projectId)}/inquiries/objects`, { query: { q }, signal }),
  assignInquiry: (projectId: string, inquiryId: string, exposeId: string) =>
    request<Inquiry>(`${project(projectId)}/inquiries/${id(inquiryId)}/assign`, { method: 'POST', body: { expose_id: exposeId } }),
  exposes: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<{ items: Expose[] }>(`${project(projectId)}/leads/${id(leadId)}/exposes`, { signal }),
  createExpose: (projectId: string, leadId: string, body: { broker_user_id?: string | null }) =>
    request<ExposeDetail>(`${project(projectId)}/leads/${id(leadId)}/exposes`, { method: 'POST', body }),
  expose: (projectId: string, exposeId: string, signal?: AbortSignal) => request<ExposeDetail>(`${project(projectId)}/exposes/${id(exposeId)}`, { signal }),
  updateExpose: (projectId: string, exposeId: string, body: ExposeInput) =>
    request<ExposeDetail>(`${project(projectId)}/exposes/${id(exposeId)}`, { method: 'PUT', body }),
  deleteExpose: (projectId: string, exposeId: string) => request<void>(`${project(projectId)}/exposes/${id(exposeId)}`, { method: 'DELETE' }),
  submitExposeApproval: (projectId: string, exposeId: string) =>
    request<ExposeDetail>(`${project(projectId)}/exposes/${id(exposeId)}/submit-approval`, { method: 'POST' }),
  approveExpose: (projectId: string, exposeId: string) =>
    request<ExposeDetail>(`${project(projectId)}/exposes/${id(exposeId)}/approve`, { method: 'POST' }),
  publishExposeImmowelt: (projectId: string, exposeId: string) =>
    request<{
      expose: ExposeDetail;
      provider_id: string;
      object_number: string;
      images_count: number;
      archive_size_bytes: number;
      ftp_transferred: boolean;
      message: string;
    }>(`${project(projectId)}/exposes/${id(exposeId)}/publish-immowelt`, { method: 'POST' }),
  exposeOpenImmoZipUrl: (projectId: string, exposeId: string) => buildUrl(`${project(projectId)}/exposes/${id(exposeId)}/openimmo.zip`),
  uploadExposeImage: (projectId: string, exposeId: string, file: Blob, title: string, category: string) =>
    request<ExposeImage>(`${project(projectId)}/exposes/${id(exposeId)}/images`, { method: 'POST', body: file, query: { title, category } }),
  updateExposeImage: (projectId: string, exposeId: string, imageId: string, body: ExposeImageInput) =>
    request<ExposeImage>(`${project(projectId)}/exposes/${id(exposeId)}/images/${id(imageId)}`, { method: 'PUT', body }),
  suggestExposeTexts: (projectId: string, exposeId: string) =>
    request<ExposeTextSuggestion>(`${project(projectId)}/exposes/${id(exposeId)}/ai-texts`, { method: 'POST' }),
  editExposeImageWithAI: (projectId: string, exposeId: string, imageId: string, mode: 'ENHANCE' | 'STAGING' | 'TWILIGHT') =>
    request<ExposeImage>(`${project(projectId)}/exposes/${id(exposeId)}/images/${id(imageId)}/ai`, { method: 'POST', body: { mode } }),
  deleteExposeImage: (projectId: string, exposeId: string, imageId: string) =>
    request<void>(`${project(projectId)}/exposes/${id(exposeId)}/images/${id(imageId)}`, { method: 'DELETE' }),
  // Aufzeichnung erst nach Zustimmung des Kunden (der Telefonist bestätigt sie mit dem Start).
  startRecording: (projectId: string, callId: string) =>
    request<Call>(`${project(projectId)}/calls/${id(callId)}/recording/start`, { method: 'POST', body: { consent: true } }),
  stopRecording: (projectId: string, callId: string) => request<Call>(`${project(projectId)}/calls/${id(callId)}/recording/stop`, { method: 'POST' }),
  // Live-Hilfe (nach Zustimmung): WebSocket-Adresse für den Browser, dazu Leitfaden und Pflichtpunkte des Projekts.
  startLiveAssist: (projectId: string, callId: string) =>
    request<LiveAssistStart>(`${project(projectId)}/calls/${id(callId)}/live-assist`, { method: 'POST' }),
  stopLiveAssist: (projectId: string, callId: string) => request<void>(`${project(projectId)}/calls/${id(callId)}/live-assist`, { method: 'DELETE' }),
  // Gesprächsauswertung: Einwände, Monatsbericht, Qualität (Feature-Flag CALL_QUALITY_SCORING).
  conversationInsights: (projectId: string, days: number, signal?: AbortSignal) =>
    request<ConversationInsights>(`${project(projectId)}/insights/conversations`, { query: { days }, signal }),
  qualitySummary: (projectId: string, days: number, signal?: AbortSignal) =>
    request<QualitySummary>(`${project(projectId)}/insights/quality`, { query: { days }, signal }),
  createConversationReport: (projectId: string, month?: string) =>
    request<ConversationReport>(`${project(projectId)}/insights/reports`, { method: 'POST', body: month ? { month } : {} }),
  callQuality: (projectId: string, callId: string, signal?: AbortSignal) =>
    request<CallQualityReview>(`${project(projectId)}/calls/${id(callId)}/quality`, { signal }),
  setOutcome: (projectId: string, callId: string, body: CallOutcome) =>
    request<Call>(`${project(projectId)}/calls/${id(callId)}/outcome`, { method: 'POST', body }),
  contactState: (projectId: string, leadId: string, action: 'CLEAR_COOLDOWN' | 'BLOCK' | 'UNBLOCK', reason: string, scope?: 'PERSON' | 'HOUSEHOLD') =>
    request<void>(`${project(projectId)}/leads/${id(leadId)}/contact-state`, { method: 'POST', body: { action, reason, scope } }),

  // Wiedervorlagen.
  followUps: (projectId: string, params: FollowUpQuery = {}, signal?: AbortSignal) =>
    request<ItemList<FollowUp>>(`${project(projectId)}/followups`, { query: { ...params }, signal }),
  createFollowUp: (projectId: string, body: FollowUpCreate) => request<FollowUp>(`${project(projectId)}/followups`, { method: 'POST', body }),
  parseFollowUp: (projectId: string, text: string, signal?: AbortSignal) =>
    request<ParseResult>(`${project(projectId)}/followups/parse`, { method: 'POST', body: { text }, signal }),
  requestFollowUp: (projectId: string, body: FollowUpRequest) => request<FollowUp>(`${project(projectId)}/followups/requests`, { method: 'POST', body }),
  completeFollowUp: (projectId: string, followUpId: string, note = '') =>
    request<FollowUp>(`${project(projectId)}/followups/${id(followUpId)}/complete`, { method: 'POST', body: { note } }),
  cancelFollowUp: (projectId: string, followUpId: string, note = '') =>
    request<FollowUp>(`${project(projectId)}/followups/${id(followUpId)}/cancel`, { method: 'POST', body: { note } }),

  // Termine, Kalender, Außendienst.
  appointmentTypes: (projectId: string, signal?: AbortSignal) =>
    request<ItemList<AppointmentType>>(`${project(projectId)}/appointment-types`, { signal }),
  createAppointmentType: (projectId: string, body: AppointmentTypeInput) =>
    request<AppointmentType>(`${project(projectId)}/appointment-types`, { method: 'POST', body }),
  suggestAppointment: (projectId: string, body: AppointmentSuggestRequest, signal?: AbortSignal) =>
    request<AppointmentSuggestion>(`${project(projectId)}/appointments/suggest`, { method: 'POST', body, signal }),
  createAppointment: (projectId: string, body: AppointmentCreate) =>
    request<Appointment>(`${project(projectId)}/appointments`, { method: 'POST', body }),
  appointment: (projectId: string, appointmentId: string, signal?: AbortSignal) =>
    request<AppointmentDetail>(`${project(projectId)}/appointments/${id(appointmentId)}`, { signal }),
  rescheduleAppointment: (projectId: string, appointmentId: string, body: RescheduleInput) =>
    request<Appointment>(`${project(projectId)}/appointments/${id(appointmentId)}/reschedule`, { method: 'POST', body }),
  // next: wie es mit dem Lead weitergeht (neuer Termin, später erneut anfragen mit later_date, kein Interesse, Akquise).
  cancelAppointment: (projectId: string, appointmentId: string, reason: string, next?: { next?: string; later_date?: string }) =>
    request<Appointment>(`${project(projectId)}/appointments/${id(appointmentId)}/cancel`, { method: 'POST', body: { reason, ...next } }),
  phoneConfirmations: (projectId: string, state: 'open' | 'done' = 'open', signal?: AbortSignal) =>
    request<ItemList<PhoneConfirmationItem>>(`${project(projectId)}/appointments/phone-confirmations`, { query: { state }, signal }),
  // Terminbericht nach dem Termin (Fragenkatalog, OFFENE_FRAGEN B-29 Phase 2).
  appointmentReport: (projectId: string, appointmentId: string, signal?: AbortSignal) =>
    request<AppointmentReport>(`${project(projectId)}/appointments/${id(appointmentId)}/report`, { signal }),
  saveAppointmentReport: (projectId: string, appointmentId: string, body: AppointmentReportAnswers) =>
    request<AppointmentReport>(`${project(projectId)}/appointments/${id(appointmentId)}/report`, { method: 'PUT', body }),
  submitAppointmentReport: (projectId: string, appointmentId: string, body: AppointmentReportAnswers) =>
    request<AppointmentReport>(`${project(projectId)}/appointments/${id(appointmentId)}/report/submit`, { method: 'POST', body }),
  dueAppointmentReports: (projectId: string, signal?: AbortSignal) =>
    request<ItemList<AppointmentReportDue>>(`${project(projectId)}/field-sales/reports/due`, { signal }),
  // Partner-Pipeline (Dominik 23.10.2026): Neukunden-Akquise Maklerpass-Partner, Pipedrive nur als Inspiration.
  partnerProspects: (projectId: string, stage?: string, signal?: AbortSignal) =>
    request<ItemList<PartnerProspect>>(`${project(projectId)}/partner-prospects`, { query: stage ? { stage } : undefined, signal }),
  createPartnerProspect: (projectId: string, body: PartnerProspectInput) =>
    request<PartnerProspect>(`${project(projectId)}/partner-prospects`, { method: 'POST', body }),
  importPartnerProspects: (projectId: string, items: PartnerProspectInput[]) =>
    request<ItemList<PartnerProspect>>(`${project(projectId)}/partner-prospects/import`, { method: 'POST', body: { items } }),
  updatePartnerProspect: (projectId: string, prospectId: string, body: PartnerProspectInput) =>
    request<PartnerProspect>(`${project(projectId)}/partner-prospects/${id(prospectId)}`, { method: 'PATCH', body }),
  enrichPartnerProspect: (projectId: string, prospectId: string) =>
    request<PartnerProspect>(`${project(projectId)}/partner-prospects/${id(prospectId)}/enrich`, { method: 'POST' }),
  enrichPartnerProspectsBatch: (projectId: string, limit = 25) =>
    request<{ done: number; remaining: number }>(`${project(projectId)}/partner-prospects/enrich-batch`, { method: 'POST', query: { limit: String(limit) } }),
  partnerMailTemplates: (projectId: string, prospectId: string, signal?: AbortSignal) =>
    request<{ items: PartnerMailTemplate[]; sender: PartnerMailSender }>(`${project(projectId)}/partner-prospects/${id(prospectId)}/mail-templates`, { signal }),
  draftPartnerMail: (projectId: string, prospectId: string, body: { template_key: string; notes?: string }) =>
    request<PartnerMailDraft>(`${project(projectId)}/partner-prospects/${id(prospectId)}/mail-draft`, { method: 'POST', body }),
  previewPartnerMail: (projectId: string, prospectId: string, body: PartnerMailSend) =>
    request<{ html: string }>(`${project(projectId)}/partner-prospects/${id(prospectId)}/mail-preview`, { method: 'POST', body }),
  sendPartnerMail: (projectId: string, prospectId: string, body: PartnerMailSend) =>
    request<PartnerProspect>(`${project(projectId)}/partner-prospects/${id(prospectId)}/mail`, { method: 'POST', body }),
  startPartnerCall: (projectId: string, prospectId: string, phone?: string) =>
    request<{ call_id: string; phone: string }>(`${project(projectId)}/partner-prospects/${id(prospectId)}/call`, {
      method: 'POST',
      body: phone ? { phone } : {},
    }),
  callRecordingUrl: (projectId: string, callId: string) => buildUrl(`${project(projectId)}/calls/${id(callId)}/recording`),
  partnerProspectEvents: (projectId: string, prospectId: string, signal?: AbortSignal) =>
    request<ItemList<PartnerProspectEvent>>(`${project(projectId)}/partner-prospects/${id(prospectId)}/events`, { signal }),
  addPartnerProspectEvent: (projectId: string, prospectId: string, body: PartnerProspectEventInput) =>
    request<PartnerProspectEvent>(`${project(projectId)}/partner-prospects/${id(prospectId)}/events`, { method: 'POST', body }),
  // Kaufgesuche (Dominik 14.10.2026): Eigentümer, die selbst eine Immobilie suchen – beim Makler des Gebiets.
  buyerRequests: (projectId: string, status?: string, signal?: AbortSignal) =>
    request<ItemList<BuyerRequest>>(`${project(projectId)}/buyer-requests`, { query: status ? { status } : undefined, signal }),
  updateBuyerRequest: (projectId: string, requestId: string, body: BuyerRequestUpdate) =>
    request<BuyerRequest>(`${project(projectId)}/buyer-requests/${id(requestId)}`, { method: 'PATCH', body }),
  // Matching (14.10.2026): passende Objekte im Bestand bzw. Kaufgesuche, zu denen ein Lead passt.
  buyerRequestMatches: (projectId: string, requestId: string, signal?: AbortSignal) =>
    request<BuyerMatchList>(`${project(projectId)}/buyer-requests/${id(requestId)}/matches`, { signal }),
  leadBuyerMatches: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<LeadBuyerMatch>>(`${project(projectId)}/leads/${id(leadId)}/buyer-matches`, { signal }),
  // Fotos und Dateien zum Lead bzw. Termin (Dominik 14.10.2026: „Bilder und Dokumente müssen upload-fähig sein“).
  leadAttachments: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<LeadAttachment>>(`${project(projectId)}/leads/${id(leadId)}/attachments`, { signal }),
  uploadLeadAttachment: (projectId: string, leadId: string, file: Blob, q: { name: string; title?: string; category?: string; appointment_id?: string }) =>
    request<LeadAttachment>(`${project(projectId)}/leads/${id(leadId)}/attachments`, { method: 'POST', body: file, query: q }),
  updateLeadAttachment: (projectId: string, attachmentId: string, body: { title?: string; category?: string }) =>
    request<LeadAttachment>(`${project(projectId)}/attachments/${id(attachmentId)}`, { method: 'PATCH', body }),
  deleteLeadAttachment: (projectId: string, attachmentId: string) => request<void>(`${project(projectId)}/attachments/${id(attachmentId)}`, { method: 'DELETE' }),
  leadAttachmentUrl: (projectId: string, attachmentId: string) => buildUrl(`${project(projectId)}/attachments/${id(attachmentId)}/file`),
  recordPhoneConfirmation: (projectId: string, appointmentId: string, body: PhoneConfirmationInput) =>
    request<AppointmentDetail>(`${project(projectId)}/appointments/${id(appointmentId)}/phone-confirmation`, { method: 'POST', body }),
  setAppointmentStatus: (projectId: string, appointmentId: string, status: 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW', note = '') =>
    request<Appointment>(`${project(projectId)}/appointments/${id(appointmentId)}/status`, { method: 'POST', body: { status, note } }),
  assignAppointment: (projectId: string, appointmentId: string, body: { user_id: string; override_reason?: string; allow_conflicts?: boolean }) =>
    request<Appointment>(`${project(projectId)}/appointments/${id(appointmentId)}/assign`, { method: 'POST', body }),
  appointmentInquiry: (projectId: string, appointmentId: string, message: string) =>
    request<Ticket>(`${project(projectId)}/appointments/${id(appointmentId)}/inquiry`, { method: 'POST', body: { message } }),
  // Terminrückgabe: Der zugewiesene Makler gibt den Termin an den Support zurück (verschieben/absagen lassen, zurückgeben).
  handbackAppointment: (projectId: string, appointmentId: string, body: AppointmentHandbackRequest) =>
    request<AppointmentHandback>(`${project(projectId)}/appointments/${id(appointmentId)}/handback`, { method: 'POST', body }),
  resolveAppointmentHandback: (projectId: string, appointmentId: string, body: AppointmentHandbackResolve) =>
    request<AppointmentHandback>(`${project(projectId)}/appointments/${id(appointmentId)}/handback/resolve`, { method: 'POST', body }),
  appointmentHistory: (projectId: string, appointmentId: string, signal?: AbortSignal) =>
    request<AppointmentHistory>(`${project(projectId)}/appointments/${id(appointmentId)}/history`, { signal }),
  regenerateBrief: (projectId: string, appointmentId: string) =>
    request<StoredBriefResponse>(`${project(projectId)}/appointments/${id(appointmentId)}/brief`, { method: 'POST' }),
  sendAppointmentDossier: (projectId: string, appointmentId: string) =>
    request<DossierQueued>(`${project(projectId)}/appointments/${id(appointmentId)}/dossier`, { method: 'POST' }),
  leadAppointments: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<Appointment>>(`${project(projectId)}/leads/${id(leadId)}/appointments`, { signal }),
  fieldSalesDay: (projectId: string, date?: string, userId?: string, signal?: AbortSignal) =>
    request<DayView>(`${project(projectId)}/field-sales/day`, { query: { date, user_id: userId }, signal }),
  fieldSalesLeads: (projectId: string, signal?: AbortSignal) => request<BrokerLeadsView>(`${project(projectId)}/field-sales/leads`, { signal }),
  calendar: (projectId: string, q: CalendarQuery, signal?: AbortSignal) =>
    request<CalendarRange>(`${project(projectId)}/calendar`, { query: { ...q }, signal }),
  availability: (projectId: string, userId: string, from: string, to: string, signal?: AbortSignal) =>
    request<ItemList<CalendarSlot>>(`${project(projectId)}/availability`, { query: { user_id: userId, from, to }, signal }),
  calendarSync: (projectId: string, status?: string, signal?: AbortSignal) =>
    request<ItemList<SyncItem>>(`${project(projectId)}/calendar-sync`, { query: { status }, signal }),
  retryCalendarSync: (projectId: string, itemId: string) =>
    request<void>(`${project(projectId)}/calendar-sync/${id(itemId)}/retry`, { method: 'POST' }),

  // KI-Auswertung.
  aiReviews: (projectId: string, leadId?: string, signal?: AbortSignal) =>
    request<AIReviews>(`${project(projectId)}/ai/reviews`, { query: { lead_id: leadId }, signal }),
  acceptProposal: (projectId: string, proposalId: string) =>
    request<AIProposal>(`${project(projectId)}/ai/proposals/${id(proposalId)}/accept`, { method: 'POST' }),
  rejectProposal: (projectId: string, proposalId: string, reason = '') =>
    request<AIProposal>(`${project(projectId)}/ai/proposals/${id(proposalId)}/reject`, { method: 'POST', body: { reason } }),
  acceptAction: (projectId: string, actionId: string, resultEntityId?: string) =>
    request<AIAction>(`${project(projectId)}/ai/actions/${id(actionId)}/accept`, {
      method: 'POST',
      body: resultEntityId ? { result_entity_id: resultEntityId } : {},
    }),
  rejectAction: (projectId: string, actionId: string, reason = '') =>
    request<AIAction>(`${project(projectId)}/ai/actions/${id(actionId)}/reject`, { method: 'POST', body: { reason } }),
  leadConversationInsights: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<ItemList<import('./types').ConversationInsight>>(`${project(projectId)}/leads/${id(leadId)}/conversation-insights`, { signal }),
  callAnalysis: (projectId: string, callId: string, signal?: AbortSignal) =>
    request<CallAnalysis>(`${project(projectId)}/calls/${id(callId)}/analysis`, { signal }),
  resolveAnalysis: (projectId: string, callId: string, resolution: 'KEEP_MANUAL' | 'ACCEPT_AI') =>
    request<void>(`${project(projectId)}/calls/${id(callId)}/analysis/resolve`, { method: 'POST', body: { resolution } }),

  // Rückfragen & Aufgaben (Tickets).
  tickets: (projectId: string, q: TicketQuery = {}, signal?: AbortSignal) =>
    request<TicketPage>(`${project(projectId)}/tickets`, { query: { ...q }, signal }),
  ticket: (projectId: string, ticketId: string, signal?: AbortSignal) =>
    request<TicketDetail>(`${project(projectId)}/tickets/${id(ticketId)}`, { signal }),
  createTicket: (projectId: string, body: TicketCreate) => request<Ticket>(`${project(projectId)}/tickets`, { method: 'POST', body }),
  updateTicket: (projectId: string, ticketId: string, body: TicketUpdate) =>
    request<Ticket>(`${project(projectId)}/tickets/${id(ticketId)}`, { method: 'PATCH', body }),
  addTicketComment: (projectId: string, ticketId: string, body: string) =>
    request<TicketComment>(`${project(projectId)}/tickets/${id(ticketId)}/comments`, { method: 'POST', body: { body } }),
  editTicketComment: (projectId: string, ticketId: string, commentId: string, body: string) =>
    request<TicketComment>(`${project(projectId)}/tickets/${id(ticketId)}/comments/${id(commentId)}`, { method: 'PATCH', body: { body } }),
  ticketCategories: (projectId: string, signal?: AbortSignal) =>
    request<ItemList<TicketCategory>>(`${project(projectId)}/ticket-categories`, { signal }),
  createTicketCategory: (projectId: string, body: TicketCategoryInput) =>
    request<TicketCategory>(`${project(projectId)}/ticket-categories`, { method: 'POST', body }),
  updateTicketCategory: (projectId: string, categoryId: string, body: TicketCategoryInput) =>
    request<TicketCategory>(`${project(projectId)}/ticket-categories/${id(categoryId)}`, { method: 'PATCH', body }),

  // E-Mail am Lead.
  leadEmails: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<EmailPage>(`${project(projectId)}/leads/${id(leadId)}/emails`, { query: { limit: 30 }, signal }),
  sendLeadEmail: (projectId: string, leadId: string, body: { subject: string; body: string }) =>
    request<EmailMessage>(`${project(projectId)}/leads/${id(leadId)}/emails`, { method: 'POST', body }),
  leadEmailBounce: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<{ bounce: EmailBounce | null }>(`${project(projectId)}/leads/${id(leadId)}/email-bounce`, { signal }),
  clearLeadEmailBounce: (projectId: string, leadId: string) =>
    request<void>(`${project(projectId)}/leads/${id(leadId)}/email-bounce`, { method: 'DELETE' }),

  // Routing, Karten, Terminvorschläge.
  fieldSalesRoute: (projectId: string, date?: string, userId?: string, signal?: AbortSignal) =>
    request<DayRoute>(`${project(projectId)}/field-sales/route`, { query: { date, user_id: userId }, signal }),
  recommendSlots: (projectId: string, body: SlotRequest, signal?: AbortSignal) =>
    request<SlotRecommendation>(`${project(projectId)}/appointments/slots`, { method: 'POST', body, signal }),
  mapLeads: (projectId: string, q: MapLeadQuery, signal?: AbortSignal) =>
    request<MapLeadClusters>(`${project(projectId)}/map/leads`, { query: { ...q }, signal }),
  mapAppointments: (projectId: string, q: { from: string; to: string; user_id?: string }, signal?: AbortSignal) =>
    request<ItemList<MapAppointment>>(`${project(projectId)}/map/appointments`, { query: { ...q }, signal }),
  mapAreas: (projectId: string, signal?: AbortSignal) => request<ItemList<MapArea>>(`${project(projectId)}/map/areas`, { signal }),
  mapAreaShapes: (projectId: string, signal?: AbortSignal) => request<MapAreaShapes>(`${project(projectId)}/map/area-shapes`, { signal }),

  // Datenschutz (privacy.manage).
  privacyImpact: (projectId: string, leadId: string, signal?: AbortSignal) =>
    request<PrivacyImpact>(`${project(projectId)}/leads/${id(leadId)}/privacy/impact`, { signal }),
  privacyExport: (projectId: string, leadId: string, reason: string) =>
    request<PrivacyRequest>(`${project(projectId)}/leads/${id(leadId)}/privacy/export`, { method: 'POST', body: { reason } }),
  privacyErase: (
    projectId: string,
    leadId: string,
    body: { mode: 'ANONYMIZE' | 'DELETE'; scope: 'PERSON' | 'HOUSEHOLD'; reason: string; cancel_future_appointments: boolean },
  ) =>
    request<PrivacyRequest>(`${project(projectId)}/leads/${id(leadId)}/privacy/erase`, { method: 'POST', body }),
  privacyRequests: (projectId: string, leadId?: string, signal?: AbortSignal) =>
    request<PrivacyRequestPage>(`${project(projectId)}/privacy-requests`, { query: { lead_id: leadId }, signal }),
  privacyDownloadUrl: (projectId: string, requestId: string) =>
    buildUrl(`${project(projectId)}/privacy-requests/${id(requestId)}/download`),
  // Auftragspreis (OFFENE_FRAGEN B-22): Preisliste, PDF und Vertragsmodell je Makler.
  priceList: (projectId: string, userId?: string, signal?: AbortSignal) =>
    request<PriceList>(`${project(projectId)}/price-list`, { query: { user_id: userId }, signal }),
  priceListPdfUrl: (projectId: string, userId?: string) => buildUrl(`${project(projectId)}/price-list/pdf`, { user_id: userId }),
  myBrokerContract: (projectId: string, signal?: AbortSignal) =>
    request<BrokerContractView>(`${project(projectId)}/broker-contracts/me`, { signal }),
  brokerContract: (projectId: string, userId: string, signal?: AbortSignal) =>
    request<BrokerContractView>(`${project(projectId)}/broker-contracts/${id(userId)}`, { signal }),
  setBrokerContract: (projectId: string, userId: string, body: BrokerContractInput) =>
    request<BrokerContractView>(`${project(projectId)}/broker-contracts/${id(userId)}`, { method: 'PUT', body }),
  appointmentMandate: (projectId: string, appointmentId: string, signal?: AbortSignal) =>
    request<MandateView>(`${project(projectId)}/appointments/${id(appointmentId)}/mandate`, { signal }),
  reportMandate: (projectId: string, appointmentId: string, body: MandateInput) =>
    request<MandateView>(`${project(projectId)}/appointments/${id(appointmentId)}/mandate`, { method: 'POST', body }),
  mandates: (projectId: string, brokerUserId?: string, signal?: AbortSignal) =>
    request<ItemList<Mandate>>(`${project(projectId)}/mandates`, { query: { broker_user_id: brokerUserId }, signal }),
  cancelMandate: (projectId: string, mandateId: string, reason: string) =>
    request<Mandate>(`${project(projectId)}/mandates/${id(mandateId)}/cancel`, { method: 'POST', body: { reason } }),
  // Rechnungsstellung (OFFENE_FRAGEN B-22): Entwurf je Auftrag, freie Rechnung, Freigabe, Zahlung, Storno.
  invoices: (projectId: string, status?: string, signal?: AbortSignal) =>
    request<ItemList<Invoice>>(`${project(projectId)}/invoices`, { query: { status }, signal }),
  invoice: (projectId: string, invoiceId: string, signal?: AbortSignal) => request<Invoice>(`${project(projectId)}/invoices/${id(invoiceId)}`, { signal }),
  createInvoice: (projectId: string, body: InvoiceDraftInput) => request<Invoice>(`${project(projectId)}/invoices`, { method: 'POST', body }),
  updateInvoice: (projectId: string, invoiceId: string, body: InvoiceDraftInput) =>
    request<Invoice>(`${project(projectId)}/invoices/${id(invoiceId)}`, { method: 'PATCH', body }),
  discardInvoice: (projectId: string, invoiceId: string) => request<void>(`${project(projectId)}/invoices/${id(invoiceId)}`, { method: 'DELETE' }),
  issueInvoice: (projectId: string, invoiceId: string) =>
    request<InvoiceIssueResult>(`${project(projectId)}/invoices/${id(invoiceId)}/issue`, { method: 'POST' }),
  markInvoicePaid: (projectId: string, invoiceId: string, paidOn: string, note: string) =>
    request<Invoice>(`${project(projectId)}/invoices/${id(invoiceId)}/paid`, { method: 'POST', body: { paid_on: paidOn, note } }),
  unmarkInvoicePaid: (projectId: string, invoiceId: string) => request<Invoice>(`${project(projectId)}/invoices/${id(invoiceId)}/paid`, { method: 'DELETE' }),
  cancelInvoice: (projectId: string, invoiceId: string, reason: string) =>
    request<InvoiceIssueResult>(`${project(projectId)}/invoices/${id(invoiceId)}/cancel`, { method: 'POST', body: { reason } }),
  invoicePdfUrl: (projectId: string, invoiceId: string) => buildUrl(`${project(projectId)}/invoices/${id(invoiceId)}/pdf`),
  removeMember: (projectId: string, membershipId: string) =>
    request<void>(`${project(projectId)}/members/${id(membershipId)}`, { method: 'DELETE' }),
  addMember: (projectId: string, body: { user_id: string; role_key: string }) =>
    request<void>(`${project(projectId)}/members`, { method: 'POST', body }),

  // Benutzerbezogen: Benachrichtigungen, Route, Passwort.
  notificationSummary: (signal?: AbortSignal) => request<NotificationSummary>('/me/notifications/summary', { signal }),
  notificationSettings: (signal?: AbortSignal) => request<UserNotificationSettings>('/me/notification-settings', { signal }),
  saveNotificationSettings: (body: UserNotificationSettingsInput) =>
    request<UserNotificationSettings>('/me/notification-settings', { method: 'PUT', body }),
  routeSettings: (signal?: AbortSignal) => request<RouteSettings>('/me/route-settings', { signal }),
  saveRouteSettings: (body: RouteSettings) => request<RouteSettings>('/me/route-settings', { method: 'PUT', body }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/me/password', { method: 'POST', body: { current_password: currentPassword, new_password: newPassword } }),

  // Vertriebsstruktur (projektübergreifend; Sichtbarkeit prüft der Server je Recht).
  salesMe: (signal?: AbortSignal) => request<SalesOverview>('/sales/me', { signal }),
  salesTree: (rootUserId?: string, signal?: AbortSignal) => request<ItemList<SalesNode>>('/sales/tree', { query: { root_user_id: rootUserId }, signal }),
  salesCommissions: (params: { user_id?: string; structure?: 'true'; from?: string; to?: string } = {}, signal?: AbortSignal) =>
    request<CommissionList>('/sales/commissions', { query: { ...params }, signal }),
  salesCandidates: (q: string, signal?: AbortSignal) => request<ItemList<SalesPerson>>('/sales/candidates', { query: { q }, signal }),
  placeSalesPosition: (userId: string, body: SalesPlace) => request<SalesNode>(`/sales/positions/${id(userId)}`, { method: 'PUT', body }),
  endSalesPosition: (userId: string) => request<void>(`/sales/positions/${id(userId)}`, { method: 'DELETE' }),

  // Administration (global).
  projects: (signal?: AbortSignal) => request<ItemList<Project>>('/projects', { signal }),
  createProject: (body: ProjectCreate) => request<Project>('/projects', { method: 'POST', body }),
  users: (params: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<{ items: UserSummary[]; next_cursor: string | null; has_more: boolean }>('/users', { query: { ...params }, signal }),
  createUser: (body: { first_name: string; last_name: string; email: string; password?: string }) =>
    request<UserSummary>('/users', { method: 'POST', body: { timezone: 'Europe/Berlin', language: 'de', ...body } }),
  updateUser: (userId: string, body: { status?: 'ACTIVE' | 'DISABLED' | 'INVITED' }) =>
    request<UserSummary>(`/users/${id(userId)}`, { method: 'PATCH', body }),
  userRoles: (userId: string, signal?: AbortSignal) => request<{ role_keys: string[] }>(`/users/${id(userId)}/roles`, { signal }),
  setUserRoles: (userId: string, roleKeys: string[]) =>
    request<{ role_keys: string[] }>(`/users/${id(userId)}/roles`, { method: 'PUT', body: { role_keys: roleKeys } }),
  resetPassword: (userId: string, password: string) =>
    request<void>(`/users/${id(userId)}/password`, { method: 'POST', body: { password } }),
  // Einladung (OFFENE_FRAGEN B-22): Link zum Passwort festlegen statt Startpasswort.
  inviteUser: (userId: string, message?: string) =>
    request<UserInvitation>(`/users/${id(userId)}/invitations`, { method: 'POST', body: { message: message ?? '' } }),
  invitation: (token: string, signal?: AbortSignal) => request<InvitationInfo>(`/auth/invitations/${id(token)}`, { signal }),
  acceptInvitation: (token: string, password: string) =>
    request<void>(`/auth/invitations/${id(token)}`, { method: 'POST', body: { password } }),
  roles: (signal?: AbortSignal) => request<ItemList<Role>>('/roles', { signal }),
  systemStatus: (signal?: AbortSignal) => request<SystemStatus>('/admin/system/status', { signal }),
  integrations: (signal?: AbortSignal) => request<ItemList<IntegrationStatus>>('/admin/integrations', { signal }),
  featureFlags: (signal?: AbortSignal) => request<ItemList<FeatureFlag>>('/admin/feature-flags', { signal }),
  setFeatureFlag: (key: string, enabled: boolean) => request<FeatureFlag>(`/admin/feature-flags/${id(key)}`, { method: 'PUT', body: { enabled } }),
  // Microsoft 365: Postfächer über die App, Stand des Bookings-Abgleichs.
  microsoftMailboxes: (signal?: AbortSignal) =>
    request<{ items: MailboxConnection[]; available: boolean }>('/admin/integrations/microsoft/mailboxes', { signal }),
  connectMicrosoftMailbox: (body: { user_id: string; mailbox: string }) =>
    request<CalendarConnection>('/admin/integrations/microsoft/mailboxes', { method: 'POST', body }),
  disconnectMicrosoftMailbox: (userId: string) => request<void>(`/admin/integrations/microsoft/mailboxes/${id(userId)}`, { method: 'DELETE' }),
  bookingsStatus: (signal?: AbortSignal) => request<BookingsStatus>('/admin/integrations/microsoft/bookings', { signal }),
  syncBookings: () => request<{ queued: boolean }>('/admin/integrations/microsoft/bookings/sync', { method: 'POST' }),
  adminEmails: (filter: 'all' | 'unassigned' | 'failed' | 'queued' = 'all', signal?: AbortSignal) =>
    request<EmailPage>('/admin/emails', { query: { filter }, signal }),
  triggerIntake: () => request<{ job_id: string }>('/admin/emails/intake', { method: 'POST' }),
  assignEmail: (emailId: string, body: { project_id: string; lead_id?: string; ticket_id?: string }) =>
    request<EmailMessage>(`/admin/emails/${id(emailId)}/assign`, { method: 'POST', body }),
  emailTemplates: (signal?: AbortSignal) => request<{ items: EmailTemplate[]; placeholders: string[] }>('/admin/email-templates', { signal }),
  saveEmailTemplate: (key: string, body: { subject: string; body_text: string; is_active?: boolean }) =>
    request<EmailTemplate>(`/admin/email-templates/${id(key)}`, { method: 'PUT', body }),
  previewEmailTemplate: (body: { subject: string; body_text: string }) =>
    request<{ subject: string; body_text: string }>('/admin/email-templates/preview', { method: 'POST', body }),
  sendTestEmail: (key: string) => request<EmailMessage>(`/admin/email-templates/${id(key)}/test`, { method: 'POST' }),
};

export interface TicketQuery {
  status?: string[];
  priority?: string[];
  category?: string;
  kind?: 'REQUEST' | 'TASK';
  assigned?: string;
  role?: string;
  due?: 'overdue' | 'today' | 'week';
  lead_id?: string;
  appointment_id?: string;
  area_id?: string;
  field_sales_user_id?: string;
  created_by?: 'me';
  q?: string;
  sort?: 'activity' | 'priority';
  cursor?: string;
  limit?: number;
}

export interface MapLeadQuery {
  south?: number;
  west?: number;
  north?: number;
  east?: number;
  zoom?: number;
  county_id?: string;
  area_id?: string;
  status?: string;
}

